export type ItemKind =
  | "boolean"
  | "select"
  | "multiselect"
  | "text"
  | "date"
  | "number"
  | "geometry"
  | "geocoding"
  | "tool_call";

export type PayloadType = "clarification" | "frontend_tools";

export interface ChoiceOption {
  id: string;
  label: string;
}

/**
 * Reserved option id for the universal HITL refusal (blackboard HITL contract §4).
 * Blackboard SELECT asks always append it as the LAST option; answering any item
 * with this value is the engine-side CANCEL — the task closes FAILED (refused),
 * remaining asks never fire and the plan ends cancelled. Both producers treat the
 * sentinel as a refusal, so the UI must never invent option ids equal to it.
 */
export const HITL_NONE_OPTION_ID = "__none__";

/**
 * The engine may stamp producer-private keys into `metadata` (unknown keys are
 * ignored downstream):
 * - `issue_codes`: `string[]` — why the ask exists (e.g. LOW_CONFIDENCE,
 *   AMBIGUOUS_EVIDENCE, MISSING_INPUT);
 * - `binding_detail`: serialized weak-candidate detail —
 *   `{ candidates?: [{ role, evidence_id, label, confidence, reasoning }] }` —
 *   the per-candidate judge reasoning, matched against option ids;
 * - `role` / `evidence_id`: slot role + candidate id for boolean confirms and picks;
 * - `plan_surfaces`: number — how many times this task has already been asked;
 * - `hitl_delivered_relays`: relay bookkeeping (ignore).
 */
export function hitlIssueCodes(item: Pick<InterruptItem, "metadata">): string[] {
  const raw = (item.metadata ?? {}).issue_codes;
  return Array.isArray(raw) ? raw.filter((v): v is string => typeof v === "string") : [];
}

/**
 * A single field requiring user input (or auto-execution) within an interrupt.
 * `id` is producer-defined and MUST be echoed back verbatim — blackboard
 * round-scopes it (`{task_id}:{round}` / `{task_id}:{round}:{data_kind}`, a re-ask
 * bumps the round so stale answers are ignored) while plan-execute uses
 * `{task_id}__{key}`. Never parse the id or assume it repeats across rounds.
 */
export interface InterruptItem {
  id: string;
  task_id: string;
  subagent_type: string;
  key: string;
  kind: ItemKind;
  display_text: string;
  placeholder: string;
  options: ChoiceOption[];
  is_confirmation: boolean;
  tool_name: string;
  tool_args: Record<string, unknown>;
  /** Optional private slot from the producer (e.g. capability_id, resolver_id, issue_codes); unknown keys are ignored (doc 31 §8.1). */
  metadata?: Record<string, unknown>;
}

/**
 * The interrupt payload emitted by the LangGraph agent.
 * display_text is shown as intro text above all fields.
 * items is an ordered flat list of fields to resolve (or tool calls to execute).
 */
export interface InterruptPayload {
  type: PayloadType;
  display_text: string;
  items: InterruptItem[];
  round: number;
}

/** One resolved answer for a single InterruptItem. */
export interface InterruptResolvedItem {
  id: string;
  value: string | string[];
}

/** The array sent as Command.resume to LangGraph once all entries are answered. */
export type HitlResumePayload = InterruptResolvedItem[];
